"use client";

// install (please try to align the version of installed @nivo packages)
// yarn add @nivo/pie
import { ResponsivePie } from "@nivo/pie";
import Image from "next/image";
import MetaTimeFilter from "@/components/Filters/MetaTimeFilter";
import MetaEventTypesFilter from "@/components/Filters/MetaEventTypesFilter";
import { useState } from "react";
import { IDecklistV2 } from "@/models/decklist";
import { useLeaders } from "@/hooks/useLeaders";
import { CDN } from "@/lib/cdn";
import { SupportedGames } from "@/types/game";
import {
  deriveArchetypeFromDecklist,
  getArchetypeFromCache,
} from "@/lib/games/palworld/archetypeManager";
import { getCardData } from "@/lib/games/palworld/getCardDataLocal";
import { LeaderSquareWithBorder } from "@/components/leaders/LeaderSquareWithBorder";

const COLORS = {
  red: "#d81b60", // Darker, softer red
  green: "#68b52a", // Muted green
  blue: "#009bb3", // Softened blue
  purple: "#8e6fbf", // Slightly muted purple
  black: "#3e3c3a", // Softer black
  yellow: "#e6c90d", // Mellowed yellow
  white: "#f5f5f5", // Off-white for a softer effect
  slate: "#b0b8c5", // Muted slate
};

// bright
// const COLORS = {
//   red: "#f92672",
//   green: "#79e22e",
//   blue: "#00b3e6",
//   purple: "#ae81ff",
//   black: "#4f4d4b",
//   yellow: "#ffe010",
//   white: "white",
//   slate: "#cbd5e1",
// };

// const COLORS = {
//   red: "#C2OE4D",
//   green: "#12A150",
//   blue: "#006FEE",
//   purple: "#6020A0",
//   black: "#4f4d4b",
//   yellow: "#F5A524",
//   white: "white",
//   slate: "#cbd5e1",
// };

const theme = {
  labels: {
    text: {
      fontSize: 14,
      outlineWidth: 1,
      outlineColor: "black",
    },
  },
};

type ColorKey = keyof typeof COLORS;

// make sure parent container have a defined height when using
// responsive component, otherwise height will be 0 and
// no chart will be rendered.
// website examples showcase many properties,
// you'll often use just a few of them.
export default function LeaderPieChartWithFilters({
  decklists,
  game,
  palworldArchetypes,
}: {
  decklists: IDecklistV2[];
  game?: SupportedGames;
  palworldArchetypes?: Record<string, any>;
}) {
  const { leaders, isLoading, error } = useLeaders();

  // Debug logging for leaders loading
  if (error) {
    console.error("[LeaderPieChart] Error loading leaders:", error);
  }
  if (isLoading) {
    console.log("[LeaderPieChart] Loading leaders...");
  }
  if (!isLoading && Object.keys(leaders).length === 0) {
    console.warn("[LeaderPieChart] Leaders loaded but empty!");
  }
  const defaultEventFilter = game === "palworld" ? "All" : "Flagship+";
  const [timeFilter, setTimeFilter] = useState(new Set(["2 Weeks"]));
  const [eventFilter, setEventFilter] = useState(new Set([defaultEventFilter]));

  const filteredDecklists = getData(
    decklists,
    timeFilter.values().next().value,
    eventFilter.values().next().value,
    game,
  );

  const pieChartData = getPieChartDataFromDecklists(
    filteredDecklists,
    game,
    palworldArchetypes,
    leaders,
  );

  return (
    <div className="rounded-md p-4 px-[6px]">
      <div className="px-[6px]">
        <div className="text-lg font-semibold mb-2 text-center">
          Current Meta Decklists
        </div>
        <div className="flex flex-row gap-2 justify-center mb-1">
          <MetaTimeFilter selected={timeFilter} setSelected={setTimeFilter} />
          <MetaEventTypesFilter
            selected={eventFilter}
            setSelected={setEventFilter}
            game={game}
          />
        </div>
      </div>
      <div className="w-full flex items-center justify-center">
        <LeaderPieChart data={pieChartData} />
      </div>
    </div>
  );
}

export function LeaderPieChart({
  data,
  tooltipLabel = "Decklists",
}: {
  data: PieChartLeader[];
  tooltipLabel?: string;
}) {
  let totalCount = 0;
  for (const d of data) {
    totalCount += d.value;
  }
  return (
    <div className="relative h-[240px] w-[240px]">
      {totalCount === 0 ? (
        <div className="w-full h-full flex items-center justify-center italic">
          Not enough decklists data
        </div>
      ) : (
        <ResponsivePie
          theme={theme}
          data={data}
          sortByValue={true}
          margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
          innerRadius={0.5}
          padAngle={0.7}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          borderWidth={1}
          colors={({ data }) => COLORS[data.color as ColorKey]}
          borderColor={{
            from: "color",
            modifiers: [["darker", 0.2]],
          }}
          motionConfig="stiff"
          enableArcLinkLabels={false}
          arcLinkLabelsSkipAngle={20}
          arcLinkLabelsStraightLength={10}
          arcLinkLabelsDiagonalLength={10}
          arcLinkLabelsTextColor={COLORS.slate}
          arcLinkLabel={(e) => e.label + " (" + e.value + ")"}
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          arcLabelsSkipAngle={20}
          arcLabelsTextColor={COLORS.white}
          arcLabel={(e) => `${((e.value / totalCount) * 100).toFixed(0)}%`}
          // arcLabelsComponent={({ datum }) => {
          //   return (
          //     <div style={{ color: "#fff" }}>
          //       {datum.data.id}{" "}
          //       {/* Assuming you want to show the `id` of the datum */}
          //     </div>
          //   );
          // }}
          defs={[
            {
              id: "gradientRG",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.red },
                { offset: 50, color: COLORS.green },
              ],
            },
            {
              id: "gradientRU",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.red },
                { offset: 50, color: COLORS.blue },
              ],
            },
            {
              id: "gradientRP",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.red },
                { offset: 50, color: COLORS.purple },
              ],
            },
            {
              id: "gradientRB",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.red },
                { offset: 50, color: COLORS.black },
              ],
            },
            {
              id: "gradientRY",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.red },
                { offset: 50, color: COLORS.yellow },
              ],
            },
            {
              id: "gradientGU",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.green },
                { offset: 50, color: COLORS.blue },
              ],
            },
            {
              id: "gradientGB",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.green },
                { offset: 50, color: COLORS.black },
              ],
            },
            {
              id: "gradientGP",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.green },
                { offset: 50, color: COLORS.purple },
              ],
            },
            {
              id: "gradientGY",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.green },
                { offset: 50, color: COLORS.yellow },
              ],
            },
            {
              id: "gradientUP",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.blue },
                { offset: 50, color: COLORS.purple },
              ],
            },
            {
              id: "gradientUB",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.blue },
                { offset: 50, color: COLORS.black },
              ],
            },
            {
              id: "gradientUY",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.blue },
                { offset: 50, color: COLORS.yellow },
              ],
            },
            {
              id: "gradientPB",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.purple },
                { offset: 50, color: COLORS.black },
              ],
            },
            {
              id: "gradientPY",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.purple },
                { offset: 50, color: COLORS.yellow },
              ],
            },
            {
              id: "gradientBY",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.black },
                { offset: 50, color: COLORS.yellow },
              ],
            },
            // Reversed gradients for Palworld where color order may differ
            {
              id: "gradientGR",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.green },
                { offset: 50, color: COLORS.red },
              ],
            },
            {
              id: "gradientUR",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.blue },
                { offset: 50, color: COLORS.red },
              ],
            },
            {
              id: "gradientPR",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.purple },
                { offset: 50, color: COLORS.red },
              ],
            },
            {
              id: "gradientBR",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.black },
                { offset: 50, color: COLORS.red },
              ],
            },
            {
              id: "gradientYR",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.yellow },
                { offset: 50, color: COLORS.red },
              ],
            },
            {
              id: "gradientUG",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.blue },
                { offset: 50, color: COLORS.green },
              ],
            },
            {
              id: "gradientBG",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.black },
                { offset: 50, color: COLORS.green },
              ],
            },
            {
              id: "gradientPG",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.purple },
                { offset: 50, color: COLORS.green },
              ],
            },
            {
              id: "gradientYG",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.yellow },
                { offset: 50, color: COLORS.green },
              ],
            },
            {
              id: "gradientPU",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.purple },
                { offset: 50, color: COLORS.blue },
              ],
            },
            {
              id: "gradientBU",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.black },
                { offset: 50, color: COLORS.blue },
              ],
            },
            {
              id: "gradientYU",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.yellow },
                { offset: 50, color: COLORS.blue },
              ],
            },
            {
              id: "gradientBP",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.black },
                { offset: 50, color: COLORS.purple },
              ],
            },
            {
              id: "gradientYP",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.yellow },
                { offset: 50, color: COLORS.purple },
              ],
            },
            {
              id: "gradientYB",
              type: "linearGradient",
              colors: [
                { offset: 49, color: COLORS.yellow },
                { offset: 50, color: COLORS.black },
              ],
            },
          ]}
          fill={[
            {
              match: (d) => d.data.allColors === "Red/Green",
              id: "gradientRG",
            },
            {
              match: (d) => d.data.allColors === "Red/Blue",
              id: "gradientRU",
            },
            {
              match: (d) => d.data.allColors === "Red/Purple",
              id: "gradientRP",
            },
            {
              match: (d) => d.data.allColors === "Red/Black",
              id: "gradientRB",
            },
            {
              match: (d) => d.data.allColors === "Red/Yellow",
              id: "gradientRY",
            },
            {
              match: (d) => d.data.allColors === "Green/Blue",
              id: "gradientGU",
            },
            {
              match: (d) => d.data.allColors === "Green/Purple",
              id: "gradientGP",
            },
            {
              match: (d) => d.data.allColors === "Green/Black",
              id: "gradientGB",
            },
            {
              match: (d) => d.data.allColors === "Green/Yellow",
              id: "gradientGY",
            },
            {
              match: (d) => d.data.allColors === "Blue/Purple",
              id: "gradientUP",
            },
            {
              match: (d) => d.data.allColors === "Blue/Black",
              id: "gradientUB",
            },
            {
              match: (d) => d.data.allColors === "Blue/Yellow",
              id: "gradientUY",
            },
            {
              match: (d) => d.data.allColors === "Purple/Black",
              id: "gradientPB",
            },
            {
              match: (d) => d.data.allColors === "Purple/Yellow",
              id: "gradientPY",
            },
            {
              match: (d) => d.data.allColors === "Black/Yellow",
              id: "gradientBY",
            },
            // Reversed color combinations
            {
              match: (d) => d.data.allColors === "Green/Red",
              id: "gradientGR",
            },
            {
              match: (d) => d.data.allColors === "Blue/Red",
              id: "gradientUR",
            },
            {
              match: (d) => d.data.allColors === "Purple/Red",
              id: "gradientPR",
            },
            {
              match: (d) => d.data.allColors === "Black/Red",
              id: "gradientBR",
            },
            {
              match: (d) => d.data.allColors === "Yellow/Red",
              id: "gradientYR",
            },
            {
              match: (d) => d.data.allColors === "Blue/Green",
              id: "gradientUG",
            },
            {
              match: (d) => d.data.allColors === "Black/Green",
              id: "gradientBG",
            },
            {
              match: (d) => d.data.allColors === "Purple/Green",
              id: "gradientPG",
            },
            {
              match: (d) => d.data.allColors === "Yellow/Green",
              id: "gradientYG",
            },
            {
              match: (d) => d.data.allColors === "Purple/Blue",
              id: "gradientPU",
            },
            {
              match: (d) => d.data.allColors === "Black/Blue",
              id: "gradientBU",
            },
            {
              match: (d) => d.data.allColors === "Yellow/Blue",
              id: "gradientYU",
            },
            {
              match: (d) => d.data.allColors === "Black/Purple",
              id: "gradientBP",
            },
            {
              match: (d) => d.data.allColors === "Yellow/Purple",
              id: "gradientYP",
            },
            {
              match: (d) => d.data.allColors === "Yellow/Black",
              id: "gradientYB",
            },
          ]}
          tooltip={(e) => (
            <LeaderTooltip
              datum={e.datum}
              totalCount={totalCount}
              tooltipLabel={tooltipLabel}
            />
          )}
        />
      )}
    </div>
  );
}

function getLeaderDataCounts(decklists: IDecklistV2[]): {
  [key: string]: number;
} {
  const leaderDataCounts: { [key: string]: number } = {};
  for (const d of decklists) {
    const leaderId = d.leader_id;
    if (leaderId) {
      if (!leaderDataCounts[leaderId]) {
        leaderDataCounts[leaderId] = 1;
      } else {
        leaderDataCounts[leaderId] += 1;
      }
    }
  }
  return leaderDataCounts;
}

function ArcLabel({ e }: { e: any }) {
  return (
    <div style={{ color: "#fff" }}>
      {" "}
      {/* Ensure contrast for visibility */}
      {e.data.label}
    </div>
  );
}

function LeaderTooltip({
  datum,
  totalCount,
  tooltipLabel = "Decklists",
}: {
  datum: any;
  totalCount: number;
  tooltipLabel?: string;
}) {
  return (
    <div className="bg-slate-800/95 p-4 rounded-lg shadow-xl min-w-[280px]">
      <div className="flex flex-row gap-4">
        <LeaderSquareWithBorder
          leaderId={datum.id}
          colors={datum?.data?.allColors}
          image={datum?.data?.image}
          size={70}
          borderSize={2}
          gameName="palworld"
        />
        <div className="flex flex-col justify-center flex-1 min-w-0">
          <div className="text-xl font-semibold truncate">{datum.label}</div>
          <div className="text-base text-slate-300">
            {tooltipLabel}: {datum.value}{" "}
            {totalCount !== 0 &&
              "(" + Math.round((datum.value / totalCount) * 100) + "%)"}
          </div>
        </div>
      </div>
    </div>
  );
}

function getData(
  decklists: IDecklistV2[],
  timeFilter: string | undefined,
  eventFilter: string | undefined,
  game?: SupportedGames,
) {
  if (decklists.length === 0) {
    return [];
  }

  const flagshipPlus = [
    "CS",
    "CS Qualifier",
    "GAO",
    "Regional",
    "Treasure Cup",
    "CS 3on3",
    "CS 5on5",
    "CS Store Qualifier",
    "Unofficial Tournament",
    "Flagship EX",
    "Flagship",
    "Store Regionals",
    "Store Treasure Cup",
  ];

  const challengersPlus = ["Masters League", "Challengers Cup"];

  let startTime = new Date(0); // Beginning of time (Unix epoch)
  const firstTime = new Date(decklists[0].date);
  if (timeFilter === "2 Weeks") {
    startTime = new Date(firstTime.getTime() - 2 * 7 * 24 * 60 * 60 * 1000); // 2 weeks ago
  } else if (timeFilter === "Month") {
    startTime = new Date(firstTime.setMonth(firstTime.getMonth() - 1)); // 1 month ago
  }

  let ret = [];

  for (const d of decklists) {
    const curDate = new Date(d.date);
    if (curDate > startTime) {
      if (eventFilter === "Flagship+") {
        if (flagshipPlus.includes(d.tournament_type)) {
          ret.push(d);
        }
      } else if (eventFilter === "Challengers+") {
        if (challengersPlus.includes(d.tournament_type)) {
          ret.push(d);
        }
      } else {
        ret.push(d);
      }
    }
  }
  return ret;
}

interface LeaderData {
  id: string;
  label: string;
  value: number;
  color: string;
  allColors: string;
  image: string;
}

/**
 * Normalize Palworld archetype names by sorting Lucky Pals by cost.
 * This ensures "Jormuntide & Chillet" and "Chillet & Jormuntide" group together.
 */
function normalizePalworldArchetypeName(
  archetypeName: string,
  decklistString: string,
): string {
  // Extract Lucky Pal names from the archetype name
  // Format is typically "Pal1 & Pal2" or just "Pal1"
  const palNames = archetypeName.split(" & ").map((name) => name.trim());

  if (palNames.length <= 1) {
    // Single pal or unknown, no need to normalize
    return archetypeName;
  }

  // Get cost for each pal from the decklist
  const palCosts: { name: string; cost: number }[] = [];
  const cards = decklistString.split(";").filter((c) => c.trim());

  for (const palName of palNames) {
    let foundCost = 999; // Default high cost if not found

    for (const card of cards) {
      const [, cardId] = card.split("x");
      if (!cardId) continue;

      const cardData = getCardData(cardId);
      if (!cardData || cardData.lucky !== "☆") continue;

      const mainName = cardData.name
        ? cardData.name.split(/[–—]/)[0].trim()
        : "";

      if (mainName === palName) {
        foundCost = cardData.cost ?? 999;
        break;
      }
    }

    palCosts.push({ name: palName, cost: foundCost });
  }

  // Sort by cost (cheap to expensive)
  palCosts.sort((a, b) => a.cost - b.cost);

  // Return normalized name
  return palCosts.map((p) => p.name).join(" & ");
}

export function getPieChartDataFromDecklists(
  decklists: IDecklistV2[],
  game?: SupportedGames,
  palworldArchetypes?: Record<string, any>,
  leaders?: Record<string, any>,
): PieChartLeader[] {
  let counts: { [key: string]: LeaderData } = {};

  for (const decklist of decklists) {
    let leaderData;
    let archetypeKey: string;

    if (game === "palworld") {
      // For Palworld, use leader_id from database if available
      if (decklist.leader_id && palworldArchetypes) {
        // Look up the archetype from the passed-in data
        const archetypeData = palworldArchetypes[decklist.leader_id];
        if (archetypeData) {
          leaderData = archetypeData;
          archetypeKey = decklist.leader_id;
        } else {
          // Fallback if archetype not found
          leaderData = deriveArchetypeFromDecklist(decklist.decklist || "");
          const normalizedName = normalizePalworldArchetypeName(
            leaderData.name,
            decklist.decklist || "",
          );
          archetypeKey = normalizedName;
          leaderData = { ...leaderData, name: normalizedName };
        }
      } else if (decklist.decklist) {
        // Fallback: derive archetype from decklist content
        leaderData = deriveArchetypeFromDecklist(decklist.decklist);
        // Normalize the archetype name by sorting the lucky pals alphabetically by cost
        // This ensures "Jormuntide & Chillet" and "Chillet & Jormuntide" are grouped together
        const normalizedName = normalizePalworldArchetypeName(
          leaderData.name,
          decklist.decklist,
        );
        archetypeKey = normalizedName;
        // Update the label to use the normalized name
        leaderData = { ...leaderData, name: normalizedName };
      } else {
        // Fallback if no decklist string
        leaderData = {
          id: decklist.leader_id,
          name: "Unknown",
          color: "Colorless",
          set: undefined,
          image: undefined,
        };
        archetypeKey = "Unknown";
      }
    } else {
      // For other games, use leader-based grouping
      const leaderId = decklist.leader_id;
      const normalizedId = leaderId?.toUpperCase();
      leaderData = leaders?.[normalizedId] || null;

      if (!leaderData) {
        leaderData = {
          id: leaderId,
          name: "Unknown",
          color: "Colorless",
          set: undefined,
          image: undefined,
        };
      }
      archetypeKey = leaderId;
    }

    const color = leaderData.color;
    const label = leaderData.name;
    // Use game-specific image path
    const image =
      game === "palworld"
        ? leaderData.image ||
          `${CDN}/games/palworld/images/archetype-squares/placeholder.webp`
        : `${CDN}/games/one-piece/images/leader-squares/${archetypeKey}.webp`;

    if (!counts[archetypeKey]) {
      counts[archetypeKey] = {
        id: archetypeKey,
        label: label,
        value: 1,
        color: color?.split("/")[0].toLowerCase() || "black",
        allColors: color,
        image: image,
      };
    } else {
      counts[archetypeKey].value += 1;
      // Keep the most complete color information (prefer multi-color over single color)
      const existingColorCount = (counts[archetypeKey].allColors || "").split(
        "/",
      ).length;
      const newColorCount = (color || "").split("/").length;

      // Always prefer multi-color over single color, or if same count, prefer the one with more info
      const shouldUpdate =
        newColorCount > existingColorCount ||
        (newColorCount === existingColorCount &&
          color &&
          color.length > (counts[archetypeKey].allColors || "").length);

      if (shouldUpdate) {
        counts[archetypeKey].allColors = color;
        counts[archetypeKey].color =
          color?.split("/")[0].toLowerCase() || "black";
      }
    }
  }

  return Object.values(counts);
}

export function getPieChartData(
  leaderDataCounts: {
    [key: string]: number;
  },
  game?: SupportedGames,
  leaders?: Record<string, any>,
): PieChartLeader[] {
  let counts: { [key: string]: LeaderData } = {};
  for (const [leaderId, count] of Object.entries(leaderDataCounts)) {
    const cardNumber = leaderId;
    if (cardNumber) {
      // For Palworld, use archetype data; for others, use leader data
      let leaderData;
      if (game === "palworld") {
        leaderData = getArchetypeFromCache(91, cardNumber);
      } else {
        const normalizedId = cardNumber?.toUpperCase();
        leaderData = leaders?.[normalizedId] || null;
      }

      // If no data found, create a fallback
      if (!leaderData) {
        leaderData = {
          id: cardNumber,
          name: "Unknown",
          color: "Colorless",
          set: undefined,
          image: undefined,
        };
      }

      const id = cardNumber;
      const color = leaderData.color;
      const label = leaderData.name;
      // Use game-specific image path
      const image =
        game === "palworld"
          ? leaderData.image ||
            `${CDN}/games/palworld/images/archetype-squares/placeholder.webp`
          : `${CDN}/games/one-piece/images/leader-squares/${cardNumber}.webp`;
      if (!counts[cardNumber]) {
        counts[cardNumber] = {
          id: id,
          label: label,
          value: count,
          color: color?.split("/")[0].toLowerCase() || "black",
          allColors: color,
          image: image,
        };
      }
    }
  }
  return Object.values(counts);
}

interface PieChartLeader {
  id: string;
  label: string;
  value: number;
  color: string;
  allColors: string;
  image: string;
}

function extractTextInParentheses(input: string): string | null {
  const match = input.match(/\(([^)]+)\)/);
  return match ? match[1] : null;
}
